import { quizdbApi } from '../api/quizdbApi';
import type { AppDispatch, AppState } from '../store/AppStore';
import { authApi } from './authEndpoints';
import type { CognitoTokens, SessionManager } from './authServices';
import { signedIn, signedOut } from './AuthStore';

export type SessionStore = { dispatch: AppDispatch; getState: () => AppState };

export interface QuizdbSessionManager extends SessionManager {
  /** Forces a refresh (shared in-flight promise); success → signedIn + token; failure → signedOut + undefined. Never throws. */
  refresh(): Promise<string | undefined>;
}

/** A token closer than this to its expiry is refreshed before use. */
const REFRESH_MARGIN_MS = 5 * 60_000;

/**
 * Holds only the access token and its expiry, in the `auth` slice; the refresh
 * token lives behind the API's httpOnly session cookie (QZ-D29). Every call
 * goes through the RTK Query endpoints (QZ-D8).
 */
export const createSessionManager = (store: SessionStore): QuizdbSessionManager => {
  let inFlightRefresh: Promise<string | undefined> | undefined;

  const runRefresh = async (): Promise<string | undefined> => {
    try {
      const { accessToken, expiresAt } = await store
        .dispatch(authApi.endpoints.refreshSession.initiate(undefined, { track: false }))
        .unwrap();
      store.dispatch(signedIn({ accessToken, expiresAt }));
      return accessToken;
    } catch {
      store.dispatch(signedOut());
      return undefined;
    }
  };

  const refresh = (): Promise<string | undefined> => {
    if (!inFlightRefresh) {
      inFlightRefresh = runRefresh().finally(() => {
        inFlightRefresh = undefined;
      });
    }
    return inFlightRefresh;
  };

  return {
    refresh,

    async start({ accessToken, refreshToken, expiresAt }: CognitoTokens): Promise<void> {
      await store
        .dispatch(
          authApi.endpoints.createSession.initiate({ accessToken, refreshToken }, { track: false })
        )
        .unwrap();
      store.dispatch(signedIn({ accessToken, expiresAt }));
    },

    async restore(): Promise<void> {
      await refresh();
    },

    async getAccessToken(): Promise<string | undefined> {
      const { status, accessToken, expiresAt } = store.getState().auth;
      if (status !== 'signedIn') {
        return undefined;
      }
      if (!accessToken || expiresAt === undefined || expiresAt - Date.now() < REFRESH_MARGIN_MS) {
        return refresh();
      }
      return accessToken;
    },

    async signOut(): Promise<void> {
      try {
        await store
          .dispatch(authApi.endpoints.signOut.initiate(undefined, { track: false }))
          .unwrap();
      } catch {
        // The local session ends regardless of whether the API could clear its cookie.
      }
      store.dispatch(signedOut());
      store.dispatch(quizdbApi.util.resetApiState());
    },
  };
};
